/**
 * PAVING (2026-10-08, docs/bim-plan/26-paving-spike.md step 1) — a driveway / walk in 3D.
 *
 * The design surface z(s, t) = profile(s) + cross(t) (kernel paving.ts) is swept along the
 * centerline: at every station sample the cross-section is laid out across the path's
 * mitered normal, so straight runs, bends and arcs all stay watertight. Faceting follows
 * the CONTROL JOINTS (the spike's core idea): each panel (joint-to-joint × strip) is one
 * smooth surface with analytic normals, and the joints are the only lines drawn across it.
 *
 * One closed solid per element (the slab: top, sides, bottom, end caps; a curb is its own
 * closed solid) feeds every 2D consumer — section poché, spot elevations, picking. The 3D
 * view swaps in PAINT PARTS: the top panels split by shade (`shade:<k>:<mat>`, the 3D
 * BRICK pseudo ids — each panel a step lighter/darker of the concrete's one color, the
 * stylized-finishes direction) plus the rest in the base color.
 */
import earcut from 'earcut'
import {
  pavingSpec, pavingCustomJoints, pavingProfile, pavingFrameAt, profileZAt, crossZAt, pavingJointStations, pavingStripOffsets,
  type PavingSpec, type PavingPins, type Pt2, type ResolvedElement,
} from '@aj/bim-kernel'
import type { ElementSolid } from '../wall/generate3d'
import { slabSolid, slopedCeilingZ } from '../slab/generate3d'
import { pointInRing } from '../roof/framing'
import { buildExistingTin, tinZAt, topoPointsOf, topoBreaklinesOf, registerTerrainPadSource } from '../topo/tin'

type V3 = [number, number, number]

/** a mesh accumulator with per-vertex normals, uvs (world feet) and 3D edge lines */
class Acc {
  pos: number[] = []
  nrm: number[] = []
  uv: number[] = []
  idx: number[] = []
  edges: number[] = []
  vc = 0
  zMin = Infinity
  zMax = -Infinity
  /** a triangle; `want` orients it (flipped if its face normal disagrees), `ns` are the
   *  per-vertex shading normals (absent = the face normal) */
  tri(a: V3, b: V3, c: V3, want: V3, ns?: [V3, V3, V3]) {
    const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2]
    const vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2]
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx
    const len = Math.hypot(nx, ny, nz)
    if (len < 1e-12) return
    nx /= len; ny /= len; nz /= len
    let p: V3[] = [a, b, c]
    let n3 = ns
    if (nx * want[0] + ny * want[1] + nz * want[2] < 0) {
      p = [a, c, b]; nx = -nx; ny = -ny; nz = -nz
      if (n3) n3 = [n3[0], n3[2], n3[1]]
    }
    for (let i = 0; i < 3; i++) {
      const q = p[i]
      const n = n3 ? n3[i] : [nx, ny, nz]
      this.pos.push(q[0], q[1], q[2])
      this.nrm.push(n[0], n[1], n[2])
      this.uv.push(q[0], q[1])
      if (q[2] < this.zMin) this.zMin = q[2]
      if (q[2] > this.zMax) this.zMax = q[2]
    }
    this.idx.push(this.vc, this.vc + 1, this.vc + 2)
    this.vc += 3
  }
  quad(a: V3, b: V3, c: V3, d: V3, want: V3, ns?: [V3, V3, V3, V3]) {
    this.tri(a, b, c, want, ns && [ns[0], ns[1], ns[2]])
    this.tri(a, c, d, want, ns && [ns[0], ns[2], ns[3]])
  }
  line(a: V3, b: V3) { this.edges.push(a[0], a[1], a[2], b[0], b[1], b[2]) }
  merge(o: Acc) {
    for (let i = 0; i < o.idx.length; i++) this.idx.push(o.idx[i] + this.vc)
    this.pos.push(...o.pos); this.nrm.push(...o.nrm); this.uv.push(...o.uv)
    this.vc += o.vc
    this.zMin = Math.min(this.zMin, o.zMin); this.zMax = Math.max(this.zMax, o.zMax)
  }
  build() {
    return {
      positions: new Float32Array(this.pos), normals: new Float32Array(this.nrm),
      indices: new Uint32Array(this.idx), uvs: new Float32Array(this.uv), edges: this.edges,
    }
  }
}

/**
 * The ribbon frame: every flattened centerline vertex carries a MITERED offset direction
 * (scaled so the edges stay parallel at the run's width, clamped at sharp bends), and a
 * point between vertices interpolates linearly — so a joint across a straight run is a
 * straight line from edge to edge, and a bend's two runs share one seam exactly.
 */
interface Ribbon {
  at(s: number, t: number): Pt2
  /** unit tangent + left normal at s (for shading normals) */
  frame(s: number): { tan: Pt2; nrm: Pt2 }
}

function makeRibbon(spec: PavingSpec): Ribbon {
  const { pts, stations } = spec
  const n = pts.length
  const segDir = (i: number): Pt2 => {
    const a = pts[i], b = pts[i + 1]
    const l = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1
    return [(b[0] - a[0]) / l, (b[1] - a[1]) / l]
  }
  const miters: Pt2[] = []
  const tans: Pt2[] = []
  for (let i = 0; i < n; i++) {
    const d0 = segDir(Math.max(0, i - 1)), d1 = segDir(Math.min(n - 2, i))
    let tx = d0[0] + d1[0], ty = d0[1] + d1[1]
    const tl = Math.hypot(tx, ty)
    if (tl < 1e-9) { tx = d1[0]; ty = d1[1] } else { tx /= tl; ty /= tl }
    tans.push([tx, ty])
    const nl: Pt2 = [-ty, tx]
    // miter scale = 1 / cos(half the turn), clamped so a hairpin can't spike
    const cos = nl[0] * -d1[1] + nl[1] * d1[0]
    const k = Math.min(3, 1 / Math.max(1 / 3, cos))
    miters.push([nl[0] * k, nl[1] * k])
  }
  const seg = (s: number): { i: number; u: number } => {
    let i = 1
    while (i < n - 1 && stations[i] < s) i++
    const len = stations[i] - stations[i - 1] || 1
    return { i: i - 1, u: Math.max(0, Math.min(1, (s - stations[i - 1]) / len)) }
  }
  return {
    at(s, t) {
      const { i, u } = seg(s)
      const a = pts[i], b = pts[i + 1], ma = miters[i], mb = miters[i + 1]
      const pa: Pt2 = [a[0] + ma[0] * t, a[1] + ma[1] * t]
      const pb: Pt2 = [b[0] + mb[0] * t, b[1] + mb[1] * t]
      return [pa[0] + (pb[0] - pa[0]) * u, pa[1] + (pb[1] - pa[1]) * u]
    },
    frame(s) {
      const { i, u } = seg(s)
      const ta = tans[i], tb = tans[i + 1]
      let tx = ta[0] + (tb[0] - ta[0]) * u, ty = ta[1] + (tb[1] - ta[1]) * u
      const l = Math.hypot(tx, ty) || 1
      tx /= l; ty /= l
      return { tan: [tx, ty], nrm: [-ty, tx] }
    },
  }
}

/**
 * The EXISTING ground (the survey surface, before any grading) at a plan point — what a
 * driveway being resurfaced actually sits on. Never the graded surface: the terrain heals
 * itself around every paving run (registerTerrainPadSource below), so reading the graded
 * surface here would read the paving's own heal back. First topo by id answers, the
 * foundation reader's convention.
 */
export function existingGroundReader(byId?: Map<string, ResolvedElement>): ((p: Pt2) => number | null) | null {
  if (!byId) return null
  const topos: ResolvedElement[] = []
  byId.forEach((e) => { if (e.category === 'topo') topos.push(e) })
  if (!topos.length) return null
  topos.sort((a, b) => (a.id < b.id ? -1 : 1))
  const tins = topos.map((t) => buildExistingTin(topoPointsOf(t), topoBreaklinesOf(t))).filter((t): t is NonNullable<typeof t> => !!t)
  if (!tins.length) return null
  return (p) => {
    for (const tin of tins) {
      const z = tinZAt(tin, p)
      if (z !== null) return z
    }
    return null
  }
}

/** where an end lands under its mode - the terrain under it, or its picked slab - or null
 *  (a level/survey end, an unpicked slab, a point the target can't answer) */
function pinZ(mode: unknown, refId: unknown, p: Pt2, byId?: Map<string, ResolvedElement>): number | null {
  if (!byId) return null
  if (mode === 'terrain') return existingGroundReader(byId)?.(p) ?? null
  if (mode !== 'slab' || typeof refId !== 'string' || !refId) return null
  const host = byId.get(refId)
  if (!host) return null
  if (host.category === 'floor') {
    const s = slabSolid(host)
    if (!s || !s.outlines.length) return null
    const sloped = slopedCeilingZ(host)
    // a pin at a slab's edge (the garage door line) still reads it: the nearest point of
    // the slab answers within a foot
    if (!pointInRing(p, s.outlines[0]) && distToRing(p, s.outlines[0]) > 1) return null
    return sloped ? sloped.top(p) : s.zTop
  }
  return null
}

function distToRing(p: Pt2, ring: Pt2[]): number {
  let best = Infinity
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length]
    const dx = b[0] - a[0], dy = b[1] - a[1]
    const l2 = dx * dx + dy * dy || 1
    const u = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2))
    best = Math.min(best, Math.hypot(p[0] - a[0] - dx * u, p[1] - a[1] - dy * u))
  }
  return best
}

/** both ends' pins, resolved against the model (the kernel spec only knows unpinned z) */
export function pavingPins(el: ResolvedElement, spec: PavingSpec, byId?: Map<string, ResolvedElement>): PavingPins {
  return {
    startZ: pinZ(el.params.startMode, el.params.startRefId, spec.pts[0], byId),
    endZ: pinZ(el.params.endMode, el.params.endRefId, spec.pts[spec.pts.length - 1], byId),
  }
}

/**
 * BEST FIT TO THE EXISTING TERRAIN (2026-10-12, Matt on the Foulger driveway: "the terrain is
 * warping in two directions and I'd like the driveway to find the best fit to that existing
 * terrain while coming out clean … we'll be resurfacing the driveway but not completely
 * re-grading it"). At every control joint the run reads the existing ground across its whole
 * width (three rows straddling the joint, so one bump can't kink it) and least-squares fits a
 * straight line: a height at the centerline and a cross slope. Between joints both are linear,
 * so each panel is the ruled surface a finisher screeds between two straight forms — the twist
 * follows the ground, the joints stay straight, and nothing creases inside a panel. One light
 * smoothing pass on the heights takes out survey noise; an end set "On a slab" still lands on
 * its slab. Returns null where there is no ground to fit (the designed surface takes over).
 */
function fitToGround(
  el: ResolvedElement, spec: PavingSpec, ribbon: Ribbon, byId?: Map<string, ResolvedElement>,
): ((s: number, t: number) => number) | null {
  const ground = existingGroundReader(byId)
  if (!ground) return null
  const st = pavingJointStations(spec, pavingCustomJoints(el))
  const h = spec.widthFt / 2
  const q = Math.min(2, (spec.jointSpacingFt > 0 ? spec.jointSpacingFt : spec.lengthFt) / 4)
  const fits: { a: number; b: number }[] = []
  for (const s0 of st) {
    let n = 0, sx = 0, sz = 0, sxx = 0, sxz = 0
    for (const ds of [-q, 0, q]) {
      const s = Math.max(0, Math.min(spec.lengthFt, s0 + ds))
      for (let j = 0; j <= 8; j++) {
        const t = h - (2 * h * j) / 8
        const z = ground(ribbon.at(s, t))
        if (z === null) continue
        n++; sx += t; sz += z; sxx += t * t; sxz += t * z
      }
    }
    if (n < 3) { fits.push({ a: NaN, b: NaN }); continue }
    const den = n * sxx - sx * sx
    const b = Math.abs(den) > 1e-9 ? (n * sxz - sx * sz) / den : 0
    fits.push({ a: (sz - b * sx) / n, b })
  }
  // a joint off the survey borrows its neighbour's line; none at all = nothing to fit
  if (fits.every((f) => Number.isNaN(f.a))) return null
  for (let i = 0; i < fits.length; i++) {
    if (!Number.isNaN(fits[i].a)) continue
    let k = 1
    while (true) {
      const l = fits[i - k], r = fits[i + k]
      if (l && !Number.isNaN(l.a)) { fits[i] = { ...l }; break }
      if (r && !Number.isNaN(r.a)) { fits[i] = { ...r }; break }
      k++
    }
  }
  const a = fits.map((f) => f.a)
  const sm = a.map((v, i) => (i === 0 || i === a.length - 1 ? v : (a[i - 1] + 2 * v + a[i + 1]) / 4))
  // an end "On a slab" lands on it (the garage door line); a terrain end already is the fit
  const pins = pavingPins(el, spec, byId)
  if (el.params.startMode === 'slab' && typeof pins.startZ === 'number') sm[0] = pins.startZ
  if (el.params.endMode === 'slab' && typeof pins.endZ === 'number') sm[sm.length - 1] = pins.endZ
  const bs = fits.map((f) => f.b)
  return (s, t) => {
    let i = 1
    while (i < st.length - 1 && st[i] < s) i++
    const s0 = st[i - 1], s1 = st[i]
    const u = s1 - s0 > 1e-9 ? Math.max(0, Math.min(1, (s - s0) / (s1 - s0))) : 0
    const tc = Math.max(-h, Math.min(h, t))
    return sm[i - 1] + (sm[i] - sm[i - 1]) * u + (bs[i - 1] + (bs[i] - bs[i - 1]) * u) * tc
  }
}

/** the design surface at (s, t) */
export function pavingSurface(el: ResolvedElement, byId?: Map<string, ResolvedElement>) {
  let spec = pavingSpec(el)
  if (!spec) return null
  // a STREET's profile is its flowline; its cross section rides on top (streetSolids)
  if (spec.street) {
    const profile = pavingProfile(spec, pavingPins(el, spec, byId))
    return { spec, profile, ribbon: makeRibbon(spec), zAt: (s: number) => profileZAt(profile, s), fitted: false }
  }
  // DRIVEWAY APPROACHES (2026-10-20, docs/bim-plan/35 step 2): an end that meets a street
  // stops at the back of its sidewalk and takes its height from the street there
  const cuts = byId ? streetJunctions(byId).byRun.get(el.id) : undefined
  const pins = pavingPins(el, spec, byId)
  if (cuts?.length) {
    const s0 = cuts.find((c) => c.end === 'start')?.trimS ?? 0
    const s1 = cuts.find((c) => c.end === 'end')?.trimS ?? spec.lengthFt
    spec = trimSpec(spec, s0, s1)
    for (const c of cuts) {
      if (c.end === 'start') pins.startZ = c.z
      else pins.endZ = c.z
    }
  }
  const profile = pavingProfile(spec, pins)
  const ribbon = makeRibbon(spec)
  const fitted = el.params.surfaceFit === 'terrain' ? fitToGround(el, spec, ribbon, byId) : null
  const zAt = fitted ?? ((s: number, t: number) => profileZAt(profile, s) + crossZAt(spec, t))
  return { spec, profile, ribbon, zAt, fitted: !!fitted }
}

/** a panel's shade step, -1..1 — deterministic per panel so nothing flickers on rebuild */
const shadeOf = (k: number, j: number): number => (((k * 7 + j * 3) % 3) + 3) % 3 - 1

export function pavingSolids(el: ResolvedElement, byId?: Map<string, ResolvedElement>): ElementSolid[] {
  const surf = pavingSurface(el, byId)
  if (!surf) return []
  if (surf.spec.street) return streetSolids(el, surf.spec, surf.ribbon, surf.zAt, byId)
  const { spec, ribbon, zAt } = surf
  const h = spec.widthFt / 2
  const L = spec.lengthFt
  const mat = spec.materialId || 'default'
  const joints = pavingJointStations(spec, pavingCustomJoints(el))
  const strips = pavingStripOffsets(spec)   // left (+h) → right (−h)
  // every station the sweep must sample: joints (creases), centerline vertices (bends)
  // (rounded to merge near-duplicates, then clamped — a rounded L can land a hair past L)
  const samples = [...new Set([...joints, ...spec.stations].map((s) => Math.min(L, Math.max(0, Math.round(s * 1e6) / 1e6))))]
    .sort((a, b) => a - b)
  const P3 = (s: number, t: number, dz = 0, tz = t): V3 => {
    const p = ribbon.at(s, t)
    return [p[0], p[1], zAt(s, tz) + dz]
  }
  // the surface's own normal, its slopes measured INSIDE the panel (s0..s1 × tlo..thi) so a
  // joint stays a crease while a warped / fitted panel shades smoothly
  const topNormal = (s: number, t: number, s0: number, s1: number, tlo: number, thi: number): V3 => {
    const e = 0.05
    const sa = Math.max(s0, s - e), sb = Math.min(s1, s + e)
    const ta = Math.max(tlo, t - e), tb = Math.min(thi, t + e)
    const gs = sb - sa > 1e-9 ? (zAt(sb, t) - zAt(sa, t)) / (sb - sa) : 0
    const gt = tb - ta > 1e-9 ? (zAt(s, tb) - zAt(s, ta)) / (tb - ta) : 0
    const { tan, nrm } = ribbon.frame(s)
    const nx = -gs * tan[0] - gt * nrm[0], ny = -gs * tan[1] - gt * nrm[1]
    const l = Math.hypot(nx, ny, 1)
    return [nx / l, ny / l, 1 / l]
  }

  // ---- TOP: one smooth surface per panel, grouped by shade (3D) + into the solid (2D)
  const body = new Acc()
  const shades = new Map<number, Acc>()
  const shadeAcc = (k: number) => { let a = shades.get(k); if (!a) { a = new Acc(); shades.set(k, a) } return a }
  const UP: V3 = [0, 0, 1]
  for (let k = 0; k + 1 < joints.length; k++) {
    const s0 = joints[k], s1 = joints[k + 1]
    const ss = samples.filter((s) => s >= s0 - 1e-9 && s <= s1 + 1e-9)
    for (let j = 0; j + 1 < strips.length; j++) {
      const ta = strips[j], tb = strips[j + 1]
      const acc = shadeAcc(spec.jointSpacingFt > 0 ? shadeOf(k, j) : 0)
      for (let i = 0; i + 1 < ss.length; i++) {
        const sa = ss[i], sb = ss[i + 1]
        const q: [V3, V3, V3, V3] = [P3(sa, ta), P3(sb, ta), P3(sb, tb), P3(sa, tb)]
        const nf = (ss: number, tt: number) => topNormal(ss, tt, s0, s1, tb, ta)
        const ns: [V3, V3, V3, V3] = [nf(sa, ta), nf(sb, ta), nf(sb, tb), nf(sa, tb)]
        acc.quad(q[0], q[1], q[2], q[3], UP, ns)
        body.quad(q[0], q[1], q[2], q[3], UP, ns)
      }
    }
  }

  // ---- BODY: the cross-section below the top, swept along the run. Section points are
  // (t, dz below the design surface); the surface z under a point uses the clamped t, so a
  // thickened edge's bottom follows its own edge of the slab.
  const th = spec.thicknessFt
  const sec: Pt2[] = spec.edge === 'thickened'
    ? (() => {
        const W = spec.thickenedWidthFt, D = th + spec.thickenedDepthFt
        return [[h, 0], [h, -D], [h - W, -D], [h - W, -th], [-h + W, -th], [-h + W, -D], [-h, -D], [-h, 0]] as Pt2[]
      })()
    : [[h, 0], [h, -th], [-h, -th], [-h, 0]]
  const sweep = (acc: Acc, section: Pt2[], skipEdge: (i: number) => boolean, tzOf: (t: number) => number, capTop: number[] = []) => {
    const dzBase = 0
    for (let e = 0; e < section.length; e++) {
      if (skipEdge(e)) continue
      const a = section[e], b = section[(e + 1) % section.length]
      // outward direction of the section edge in (t, dz) — from the outline's winding (an
      // L-shaped section's middle can sit outside it, so "away from the middle" faces wrong)
      const [mt, mz] = sectionEdgeOutward(section, e)
      for (let i = 0; i + 1 < samples.length; i++) {
        const sa = samples[i], sb = samples[i + 1]
        const { nrm } = ribbon.frame((sa + sb) / 2)
        const want: V3 = [nrm[0] * mt, nrm[1] * mt, mz]
        acc.quad(
          P3(sa, a[0], a[1] + dzBase, tzOf(a[0])), P3(sb, a[0], a[1] + dzBase, tzOf(a[0])),
          P3(sb, b[0], b[1] + dzBase, tzOf(b[0])), P3(sa, b[0], b[1] + dzBase, tzOf(b[0])), want,
        )
      }
    }
    // end caps — the top edge carries the strip joints' points too, so the cap meets the
    // panels vertex for vertex (no T-junction where a longitudinal joint ends)
    const ring: Pt2[] = [...section, ...capTop.map((t) => [t, 0] as Pt2)]
    const flat = ring.flatMap((p) => [p[0], p[1]])
    const tris = earcut(flat)
    for (const [s, sign] of [[0, -1], [L, 1]] as const) {
      const { tan } = ribbon.frame(s)
      const want: V3 = [tan[0] * sign, tan[1] * sign, 0]
      const v = ring.map((p) => P3(s, p[0], p[1] + dzBase, tzOf(p[0])))
      for (let i = 0; i + 2 < tris.length; i += 3) acc.tri(v[tris[i]], v[tris[i + 1]], v[tris[i + 2]], want)
    }
  }
  const clampT = (t: number) => Math.max(-h, Math.min(h, t))
  const lastEdge = sec.length - 1   // [-h,0] → [h,0]: the top, which the panels already cover
  const sides = new Acc()
  // the cap's top runs from the section's last point (-h) back to its first (+h)
  sweep(sides, sec, (e) => e === lastEdge, clampT, strips.slice(1, -1).sort((a, b) => a - b))

  // ---- CURBS: their own closed solids outside the slab's two long edges, top level across
  let curbs: Acc | null = null
  if (spec.edge === 'curb') {
    curbs = new Acc()
    const cw = spec.curbWidthFt, ch = spec.curbHeightFt
    for (const side of [1, -1]) {
      const e = side * h
      const csec: Pt2[] = side > 0
        ? [[e + cw, ch], [e + cw, -th], [e, -th], [e, ch]]
        : [[e, ch], [e, -th], [e - cw, -th], [e - cw, ch]]
      sweep(curbs, csec, () => false, () => e)
    }
  }

  // ---- 3D EDGE LINES: the perimeter top + bottom, the corners, and every joint
  const lines = new Acc()
  const polyAlong = (t: number, dz: number, tz = t) => {
    for (let i = 0; i + 1 < samples.length; i++) lines.line(P3(samples[i], t, dz, tz), P3(samples[i + 1], t, dz, tz))
  }
  const bottomDz = spec.edge === 'thickened' ? -(th + spec.thickenedDepthFt) : -th
  polyAlong(h, 0); polyAlong(-h, 0)
  polyAlong(h, bottomDz); polyAlong(-h, bottomDz)
  for (const s of [0, L]) {
    lines.line(P3(s, h, 0), P3(s, -h, 0))
    lines.line(P3(s, h, 0), P3(s, h, bottomDz)); lines.line(P3(s, -h, 0), P3(s, -h, bottomDz))
    lines.line(P3(s, h, bottomDz), P3(s, -h, bottomDz))
  }
  for (const s of joints.slice(1, -1)) {
    for (let j = 0; j + 1 < strips.length; j++) lines.line(P3(s, strips[j], 0), P3(s, strips[j + 1], 0))
  }
  for (const t of strips.slice(1, -1)) polyAlong(t, 0)
  if (spec.edge === 'curb') {
    const cw = spec.curbWidthFt, ch = spec.curbHeightFt
    for (const side of [1, -1]) {
      const e = side * h, o = side * (h + cw)
      polyAlong(e, ch, e); polyAlong(o, ch, e); polyAlong(o, -th, e)
      for (const s of [0, L]) { lines.line(P3(s, e, ch, e), P3(s, o, ch, e)); lines.line(P3(s, o, ch, e), P3(s, o, -th, e)) }
    }
  }

  // ---- assemble: the slab (and curbs) as closed solids for every 2D consumer, the 3D
  // view's shaded panels as their paint parts
  body.merge(sides)
  const lv = el.levelId
  const gk = (part: string, m: string) => `paving|${lv}|${el.id}|${part}|${m}`
  const outline: Pt2[] = [
    ...samples.map((s) => ribbon.at(s, h)),
    ...[...samples].reverse().map((s) => ribbon.at(s, -h)),
  ]
  const baseMesh = body.build()
  const parts: NonNullable<ElementSolid['paintParts']> = []
  for (const [k, acc] of [...shades.entries()].sort((a, b) => a[0] - b[0])) {
    const m = k === 0 ? mat : `shade:${k}:${mat}`
    parts.push({ materialId: m, groupKey: gk(`top${k}`, m), mesh: acc.build() })
  }
  const sideMesh = sides.build()
  sideMesh.edges = lines.edges
  parts.push({ materialId: mat, groupKey: gk('sides', mat), mesh: sideMesh })
  const out: ElementSolid[] = [{
    elementId: el.id, category: 'paving', outlines: [outline], zBase: body.zMin, zTop: body.zMax,
    materialId: mat, groupKey: gk('slab', mat), pieces: [], placements: [], openingInstances: [],
    mesh: { positions: baseMesh.positions, normals: baseMesh.normals, indices: baseMesh.indices, edges: lines.edges, uvs: baseMesh.uvs },
    meshOnly: true, paintParts: parts,
  }]
  if (curbs) {
    const cm = curbs.build()
    out.push({
      elementId: el.id, category: 'paving', outlines: [], zBase: curbs.zMin, zTop: curbs.zMax,
      materialId: mat, groupKey: gk('curb', mat), pieces: [], placements: [], openingInstances: [],
      mesh: { positions: cm.positions, normals: cm.normals, indices: cm.indices, edges: [], uvs: cm.uvs },
      meshOnly: true,
    })
  }
  return out
}

/**
 * PAVING HEAL (2026-10-12): the terrain pad every paving run contributes — its footprint
 * (curbs included), the slab UNDERSIDE inside it (the earth under the concrete) and the slab
 * TOP at its edge (where the surrounding ground meets it). Derived from the run each build,
 * never stored; the terrain's local surgery cuts it in exactly like a graded-to slab.
 */
export function pavingTerrainPad(el: ResolvedElement, byId?: Map<string, ResolvedElement>): {
  ring: Pt2[]; under: (p: Pt2) => number; top: (p: Pt2) => number; ringUnder: number[]
} | null {
  const surf = pavingSurface(el, byId)
  if (!surf) return null
  if (surf.spec.street) return streetTerrainPad(el, surf.spec, surf.ribbon, surf.zAt, byId)
  const { spec, ribbon, zAt } = surf
  const h = spec.widthFt / 2
  const outer = spec.edge === 'curb' ? h + spec.curbWidthFt : h
  const joints = pavingJointStations(spec, pavingCustomJoints(el))
  const samples = [...new Set([...joints, ...spec.stations].map((s) => Math.min(spec.lengthFt, Math.max(0, Math.round(s * 1e6) / 1e6))))].sort((a, b) => a - b)
  const ring: Pt2[] = [...samples.map((s) => ribbon.at(s, outer)), ...[...samples].reverse().map((s) => ribbon.at(s, -outer))]
  const locate = (p: Pt2) => locateOnSpec(spec, p)
  const W = spec.thickenedWidthFt, Dx = spec.thickenedDepthFt
  const under = (p: Pt2): number => {
    const { s, t } = locate(p)
    let dz = -spec.thicknessFt
    if (spec.edge === 'thickened' && Math.abs(t) > h - W) dz -= Dx
    return zAt(s, Math.max(-h, Math.min(h, t))) + dz
  }
  const top = (p: Pt2): number => {
    const { s, t } = locate(p)
    if (spec.edge === 'curb' && Math.abs(t) > h) return zAt(s, Math.sign(t) * h) + spec.curbHeightFt
    return zAt(s, Math.max(-h, Math.min(h, t)))
  }
  return { ring, under, top, ringUnder: ring.map(under) }
}

/** the outward normal (u, dz) of a closed section's edge e, from its winding */
function sectionEdgeOutward(poly: Pt2[], e: number): [number, number] {
  let area = 0
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i], q = poly[(i + 1) % poly.length]
    area += p[0] * q[1] - q[0] * p[1]
  }
  const a = poly[e], b = poly[(e + 1) % poly.length]
  const dx = b[0] - a[0], dy = b[1] - a[1]
  // counter-clockwise: outward is to the right of travel
  return area >= 0 ? [dy, -dx] : [-dy, dx]
}

/** a plan point back to (station, signed offset) — nearest centerline segment */
function locateOnSpec(spec: PavingSpec, p: Pt2): { s: number; t: number } {
  const { pts, stations } = spec
  let best = { d: Infinity, s: 0, t: 0 }
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i], b = pts[i + 1]
    const dx = b[0] - a[0], dy = b[1] - a[1]
    const len = Math.hypot(dx, dy)
    if (len < 1e-9) continue
    const u = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / (len * len)))
    const qx = a[0] + dx * u, qy = a[1] + dy * u
    const d = Math.hypot(p[0] - qx, p[1] - qy)
    if (d < best.d) {
      // left of travel = +
      const side = dx * (p[1] - a[1]) - dy * (p[0] - a[0]) >= 0 ? 1 : -1
      best = { d, s: stations[i] + u * len, t: side * d }
    }
  }
  return { s: best.s, t: best.t }
}

/**
 * DRIVEWAY APPROACHES (2026-10-20, docs/bim-plan/35 step 2). A driveway / walk END that
 * reaches into a street's cross section (between the street paving's edge and the back of the
 * walk, a foot of slack) MEETS that street. Nothing is stored — the junction is found from the
 * two elements every build, so moving either one moves it:
 *   - the run stops where its centerline crosses the street's TRIM line (the back of walk, or the
 *     back of the apron when the apron runs deeper) and that end takes the street's surface
 *     elevation there;
 *   - a VERTICAL curb is cut for the drive's width along the street (wider on a skew), dropped to
 *     the approach lip, ramped back up over the wings; an apron climbs from the lip to the walk
 *     (the walk passes through at its own slope). A rolled curb or no curb is ridden over.
 */
export interface StreetCut {
  streetId: string
  runId: string
  end: 'start' | 'end'
  /** street station at the crossing, half the cut length along the street, the wing length */
  sc: number
  half: number
  wing: number
  /** the run station it is trimmed at, and that end's elevation */
  trimS: number
  z: number
}

const junctionMemo = new WeakMap<Map<string, ResolvedElement>, { byStreet: Map<string, StreetCut[]>; byRun: Map<string, StreetCut[]> }>()

/** where the street's surface stops for a driveway: the back of walk, or the apron's back */
function streetTrimU(st: NonNullable<PavingSpec['street']>): number {
  return st.approach ? Math.max(st.uMax, st.approach.depthFt) : st.uMax
}
/** the apron's back (u): it covers the parkway, and runs into the walk when it is deeper */
export function apronBackU(st: NonNullable<PavingSpec['street']>): number {
  return st.approach ? Math.max(st.approach.depthFt, st.walkU0) : st.backOfCurbU
}

export function streetJunctions(byId: Map<string, ResolvedElement>): { byStreet: Map<string, StreetCut[]>; byRun: Map<string, StreetCut[]> } {
  const hit = junctionMemo.get(byId)
  if (hit) return hit
  const out = { byStreet: new Map<string, StreetCut[]>(), byRun: new Map<string, StreetCut[]>() }
  junctionMemo.set(byId, out)
  const streets: { el: ResolvedElement; spec: PavingSpec; surf: NonNullable<ReturnType<typeof pavingSurface>> }[] = []
  const runs: { el: ResolvedElement; spec: PavingSpec }[] = []
  byId.forEach((el) => {
    if (el.category !== 'paving') return
    const spec = pavingSpec(el)
    if (!spec) return
    if (spec.street) {
      const surf = pavingSurface(el, byId)
      if (surf) streets.push({ el, spec, surf })
    } else runs.push({ el, spec })
  })
  if (!streets.length || !runs.length) return out
  const uOn = (S: (typeof streets)[number], p: Pt2) => {
    const { s, t } = locateOnSpec(S.spec, p)
    return { s, u: t * S.spec.street!.side }
  }
  for (const R of runs) {
    for (const end of ['start', 'end'] as const) {
      const L = R.spec.lengthFt
      const pEnd = end === 'start' ? R.spec.pts[0] : R.spec.pts[R.spec.pts.length - 1]
      for (const S of streets) {
        const st = S.spec.street!
        const at = uOn(S, pEnd)
        if (at.s <= 0.25 || at.s >= S.spec.lengthFt - 0.25) continue
        const trimU = streetTrimU(st)
        if (at.u > trimU + 1 || at.u < st.uMin - 2) continue
        // walk in from the end until the centerline crosses the trim line
        const step = 0.25
        let trimS: number | null = null
        if (at.u >= trimU) trimS = end === 'start' ? 0 : L
        else {
          let prevU = at.u
          let prevS = end === 'start' ? 0 : L
          for (let k = 1; k * step <= L; k++) {
            const s = end === 'start' ? k * step : L - k * step
            const u = uOn(S, pavingFrameAt(R.spec, s).p).u
            if (u >= trimU) { trimS = prevS + ((s - prevS) * (trimU - prevU)) / Math.max(1e-9, u - prevU); break }
            prevU = u; prevS = s
          }
        }
        if (trimS === null) continue
        const crossing = pavingFrameAt(R.spec, Math.max(0, Math.min(L, trimS)))
        const sc = uOn(S, crossing.p).s
        const stTan = S.surf.ribbon.frame(sc).tan
        const sin = Math.abs(crossing.tan[0] * stTan[1] - crossing.tan[1] * stTan[0])
        const cut: StreetCut = {
          streetId: S.el.id, runId: R.el.id, end, sc,
          half: R.spec.widthFt / 2 / Math.max(0.5, sin),
          wing: st.approach?.wingFt ?? 0,
          trimS: Math.max(0, Math.min(L, trimS)),
          z: S.surf.zAt(sc, 0) + st.topAt(trimU),
        }
        if (!out.byRun.has(R.el.id)) out.byRun.set(R.el.id, [])
        out.byRun.get(R.el.id)!.push(cut)
        if (st.approach) {
          if (!out.byStreet.has(S.el.id)) out.byStreet.set(S.el.id, [])
          out.byStreet.get(S.el.id)!.push(cut)
        }
        break
      }
    }
  }
  return out
}

/** the run between stations s0..s1 (a driveway stopped at a street's back of walk) */
function trimSpec(spec: PavingSpec, s0: number, s1: number): PavingSpec {
  const a = Math.max(0, Math.min(s0, s1)), b = Math.min(spec.lengthFt, Math.max(s0, s1))
  if (a <= 1e-6 && b >= spec.lengthFt - 1e-6) return spec
  if (b - a < 0.25) return spec
  const pts: Pt2[] = [pavingFrameAt(spec, a).p]
  for (let i = 0; i < spec.pts.length; i++) if (spec.stations[i] > a + 1e-6 && spec.stations[i] < b - 1e-6) pts.push(spec.pts[i])
  pts.push(pavingFrameAt(spec, b).p)
  const stations = [0]
  for (let i = 1; i < pts.length; i++) stations.push(stations[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]))
  return {
    ...spec, pts, stations, lengthFt: stations[stations.length - 1],
    breaks: spec.breaks.filter((x) => x.stationFt > a + 0.5 && x.stationFt < b - 0.5).map((x) => ({ ...x, stationFt: x.stationFt - a })),
  }
}

/** how far a street station sits inside its approach cuts: 1 across the drive, 0 beyond the
 *  wings, linear across a wing */
function cutBlend(cuts: StreetCut[], s: number): number {
  let f = 0
  for (const c of cuts) {
    const d = Math.abs(s - c.sc) - c.half
    const g = d <= 0 ? 1 : c.wing > 1e-6 ? Math.max(0, 1 - d / c.wing) : 0
    if (g > f) f = g
  }
  return f
}

export function streetCutsFor(el: ResolvedElement, byId?: Map<string, ResolvedElement>): StreetCut[] {
  return byId ? streetJunctions(byId).byStreet.get(el.id) ?? [] : []
}

/** the approach's section pieces at blend f: the dropped curb's top, the walk front, the apron */
function approachShape(st: NonNullable<PavingSpec['street']>) {
  const ap = st.approach!
  const bc = st.backOfCurbU
  const A = apronBackU(st)
  const zT = st.topAt(A)
  const apronZ = (u: number) => ap.lipFt + ((zT - ap.lipFt) * Math.max(0, u)) / Math.max(1e-6, A)
  return {
    A, apronZ,
    /** a curb vertex at blend f (the gutter and the curb's foot never move) */
    curbVertex: (p: Pt2, f: number): Pt2 => (p[0] >= -1e-9 && p[1] > 1e-9 ? [p[0], (1 - f) * p[1] + f * apronZ(p[0])] : p),
    walkFrontU: (f: number) => st.walkU0 + f * Math.max(0, Math.min(A, st.walkU1 - 0.05) - st.walkU0),
    apronPoly: (f: number): Pt2[] => {
      const uA = bc + f * (A - bc)
      const um = (bc + uA) / 2
      const z = (u: number) => (1 - f) * st.topAt(u) + f * apronZ(u)
      const zb = (1 - f) * st.curbHeightFt + f * apronZ(bc)
      const t = ap.thicknessFt
      return [[bc, zb], [um, z(um)], [uA, z(uA)], [uA, z(uA) - t], [um, z(um) - t], [bc, zb - t]]
    },
  }
}

/** the stations a street sweep samples: its flowline vertices, its curb shots, the walk
 *  joints and every approach's cut and wing ends */
export function streetPlanSamples(spec: PavingSpec, cuts: StreetCut[] = []): { samples: number[]; walkJoints: number[] } {
  const st = spec.street!
  const L = spec.lengthFt
  const walkJoints: number[] = []
  if (st.walkU1 > st.walkU0 && st.walkJointSpacingFt > 0) {
    const n = Math.max(1, Math.ceil(L / st.walkJointSpacingFt - 1e-6))
    for (let k = 1; k < n; k++) walkJoints.push((L * k) / n)
  }
  const cutSt = cuts.flatMap((c) => [c.sc - c.half - c.wing, c.sc - c.half, c.sc + c.half, c.sc + c.half + c.wing])
  // a wing ramps in a few steps so its curb top reads as a slope, not a fold
  const wingSt = cuts.flatMap((c) => [0.25, 0.5, 0.75].flatMap((k) => [c.sc - c.half - c.wing * k, c.sc + c.half + c.wing * k]))
  const samples = [...new Set([0, L, ...spec.stations, ...spec.breaks.map((b) => b.stationFt), ...walkJoints, ...cutSt, ...wingSt]
    .filter((s) => s >= 0 && s <= L)
    .map((s) => Math.min(L, Math.max(0, Math.round(s * 1e6) / 1e6))))].sort((a, b) => a - b)
  return { samples, walkJoints }
}

/**
 * STREETS (2026-10-20, docs/bim-plan/35): each cross-section piece — the street paving, the
 * curb and gutter, the sidewalk — is a closed (u, dz) polygon swept along the flowline ribbon
 * (lines AND arcs: the ribbon is mitered at every flattened vertex, so a curved street stays
 * watertight). Each piece is its own closed solid in its own material, so sections poché the
 * asphalt and the concrete separately. The sidewalk's score joints are a PATTERN lines piece
 * (`|pat@<spacing>`, the pattern-lines rule). Where a driveway meets it, the curb and walk
 * sections change along the street (the cut blend) and an apron is swept over the cut.
 */
function streetSolids(el: ResolvedElement, spec: PavingSpec, ribbon: Ribbon, zAt: (s: number, t: number) => number, byId?: Map<string, ResolvedElement>): ElementSolid[] {
  const st = spec.street!
  const L = spec.lengthFt
  const cuts = streetCutsFor(el, byId)
  const { samples, walkJoints } = streetPlanSamples(spec, cuts)
  const P3 = (s: number, u: number, dz: number): V3 => {
    const p = ribbon.at(s, st.side * u)
    return [p[0], p[1], zAt(s, 0) + dz]
  }
  const shape = st.approach && cuts.length ? approachShape(st) : null
  const blend = (s: number) => (shape ? cutBlend(cuts, s) : 0)
  const lv = el.levelId
  const out: ElementSolid[] = []
  /** sweep a section that may change along the street (constant vertex count) */
  const sweep = (polyAt: (s: number) => Pt2[], ss: number[], acc: Acc, lines: Acc) => {
    if (ss.length < 2) return
    const s0 = ss[0], s1 = ss[ss.length - 1]
    const n = polyAt(s0).length
    for (let i = 0; i + 1 < ss.length; i++) {
      const sa = ss[i], sb = ss[i + 1]
      const pa = polyAt(sa), pb = polyAt(sb)
      const sm = (sa + sb) / 2
      const pm = polyAt(sm)
      const { nrm } = ribbon.frame(sm)
      for (let e = 0; e < n; e++) {
        const e1 = (e + 1) % n
        // (2026-10-20, Matt: "the face of curbs are see-thru") — outward from the winding; the
        // curb-and-gutter section is L-shaped and its middle sits out over the gutter, which
        // turned the curb face inside out
        const [mu, mz] = sectionEdgeOutward(pm, e)
        const want: V3 = [nrm[0] * mu * st.side, nrm[1] * mu * st.side, mz]
        acc.quad(P3(sa, pa[e][0], pa[e][1]), P3(sb, pb[e][0], pb[e][1]), P3(sb, pb[e1][0], pb[e1][1]), P3(sa, pa[e1][0], pa[e1][1]), want)
      }
      for (let e = 0; e < n; e++) lines.line(P3(sa, pa[e][0], pa[e][1]), P3(sb, pb[e][0], pb[e][1]))
    }
    // end caps
    for (const [s, sign] of [[s0, -1], [s1, 1]] as const) {
      const sec = polyAt(s)
      const tris = earcut(sec.flatMap((p) => [p[0], p[1]]))
      const { tan } = ribbon.frame(s)
      const want: V3 = [tan[0] * sign, tan[1] * sign, 0]
      const v = sec.map((p) => P3(s, p[0], p[1]))
      for (let i = 0; i + 2 < tris.length; i += 3) acc.tri(v[tris[i]], v[tris[i + 1]], v[tris[i + 2]], want)
      for (let e = 0; e < sec.length; e++) lines.line(v[e], v[(e + 1) % sec.length])
    }
  }
  const pushSolid = (key: string, mat: string, acc: Acc, lines: Acc, ring: Pt2[]) => {
    const mesh = acc.build()
    out.push({
      elementId: el.id, category: 'paving', outlines: ring.length >= 3 ? [ring] : [], zBase: acc.zMin, zTop: acc.zMax,
      materialId: mat, groupKey: `paving|${lv}|${el.id}|${key}|${mat}`, pieces: [], placements: [], openingInstances: [],
      mesh: { positions: mesh.positions, normals: mesh.normals, indices: mesh.indices, edges: lines.edges, uvs: mesh.uvs },
      meshOnly: true,
    })
  }
  for (const piece of st.pieces) {
    const acc = new Acc()
    const lines = new Acc()
    const base = piece.poly
    const polyAt = (s: number): Pt2[] => {
      const f = blend(s)
      if (!shape || f <= 0) return base
      if (piece.key === 'curb') return base.map((p) => shape.curbVertex(p, f))
      if (piece.key === 'walk') {
        const u0 = shape.walkFrontU(f)
        return [[u0, st.topAt(u0)], base[1], base[2], [u0, st.topAt(u0) - (base[1][1] - base[2][1])]]
      }
      return base
    }
    sweep(polyAt, samples, acc, lines)
    const mat = piece.materialId || 'default'
    const lo = Math.min(...base.map((p) => p[0])), hi = Math.max(...base.map((p) => p[0]))
    const ring: Pt2[] = [...samples.map((s) => ribbon.at(s, st.side * lo)), ...[...samples].reverse().map((s) => ribbon.at(s, st.side * hi))]
    pushSolid(`street-${piece.key}`, mat, acc, lines, ring)
    if (piece.key === 'walk' && walkJoints.length) {
      const jl = new Acc()
      for (const s of walkJoints) {
        const sec = polyAt(s)
        jl.line(P3(s, sec[0][0], sec[0][1]), P3(s, sec[1][0], sec[1][1]))
      }
      let z0 = Infinity, z1 = -Infinity
      for (let i = 2; i < jl.edges.length; i += 3) { z0 = Math.min(z0, jl.edges[i]); z1 = Math.max(z1, jl.edges[i]) }
      out.push({
        elementId: el.id, category: 'paving', outlines: [], zBase: z0, zTop: z1, materialId: mat,
        groupKey: `paving|${lv}|${el.id}|street-walkjoints|pat@${Math.round(st.walkJointSpacingFt * 1000) / 1000}`,
        pieces: [], placements: [], openingInstances: [],
        mesh: { positions: new Float32Array(0), normals: new Float32Array(0), indices: new Uint32Array(0), edges: jl.edges },
        meshOnly: true, skipProjectedOutline: true,
      } as ElementSolid)
    }
  }
  // the APRONS: one closed solid per cut, from wing end to wing end
  if (shape) {
    const mat = st.pieces.find((p) => p.key === 'curb')?.materialId || 'default'
    cuts.forEach((c, k) => {
      const a = Math.max(0, c.sc - c.half - c.wing), b = Math.min(L, c.sc + c.half + c.wing)
      const ss = samples.filter((s) => s >= a - 1e-9 && s <= b + 1e-9)
      if (ss.length < 2) return
      const acc = new Acc()
      const lines = new Acc()
      sweep((s) => shape.apronPoly(blend(s)), ss, acc, lines)
      const ring: Pt2[] = [
        ...ss.map((s) => ribbon.at(s, st.side * st.backOfCurbU)),
        ...[...ss].reverse().map((s) => ribbon.at(s, st.side * (st.backOfCurbU + blend(s) * (shape.A - st.backOfCurbU)))),
      ]
      pushSolid(`street-apron${k}`, mat, acc, lines, ring)
    })
  }
  return out
}

/** a street's heal: the terrain sits under what is built and meets the lane edge and the back
 *  of walk; in the parkway it IS the surface, except under an approach's apron */
function streetTerrainPad(el: ResolvedElement, spec: PavingSpec, ribbon: Ribbon, zAt: (s: number, t: number) => number, byId?: Map<string, ResolvedElement>) {
  const st = spec.street!
  const cuts = streetCutsFor(el, byId)
  const { samples } = streetPlanSamples(spec, cuts)
  const shape = st.approach && cuts.length ? approachShape(st) : null
  const ring: Pt2[] = [
    ...samples.map((s) => ribbon.at(s, st.side * st.uMin)),
    ...[...samples].reverse().map((s) => ribbon.at(s, st.side * st.uMax)),
  ]
  const uAt = (p: Pt2) => {
    const { s, t } = locateOnSpec(spec, p)
    return { s, u: Math.max(st.uMin, Math.min(st.uMax, t * st.side)) }
  }
  const apron = (s: number, u: number): { top: number; under: number } | null => {
    if (!shape) return null
    const f = cutBlend(cuts, s)
    if (f <= 0 || u < st.backOfCurbU || u > st.backOfCurbU + f * (shape.A - st.backOfCurbU)) return null
    const top = (1 - f) * st.topAt(u) + f * shape.apronZ(u)
    return { top, under: top - st.approach!.thicknessFt }
  }
  const under = (p: Pt2) => { const { s, u } = uAt(p); return zAt(s, 0) + (apron(s, u)?.under ?? st.underAt(u)) }
  const top = (p: Pt2) => { const { s, u } = uAt(p); return zAt(s, 0) + (apron(s, u)?.top ?? st.topAt(u)) }
  return { ring, under, top, ringUnder: ring.map(under) }
}

/** the approach's plan lines + apron outline (for the street's plan pass) */
export function streetApproachPlan(
  el: ResolvedElement, spec: PavingSpec, ribbon: Ribbon, byId?: Map<string, ResolvedElement>, only?: StreetCut[],
): { lines: { pts: Pt2[]; runId: string }[]; aprons: { ring: Pt2[]; runId: string }[]; walkFrontAt: (s: number) => number } {
  const st = spec.street!
  const cuts = only ?? streetCutsFor(el, byId)
  if (!st.approach || !cuts.length) return { lines: [], aprons: [], walkFrontAt: () => st.walkU0 }
  const shape = approachShape(st)
  const { samples } = streetPlanSamples(spec, cuts)
  const L = spec.lengthFt
  const lines: { pts: Pt2[]; runId: string }[] = []
  const aprons: { ring: Pt2[]; runId: string }[] = []
  for (const c of cuts) {
    const a = Math.max(0, c.sc - c.half - c.wing), b = Math.min(L, c.sc + c.half + c.wing)
    const ss = samples.filter((s) => s >= a - 1e-9 && s <= b + 1e-9)
    const back = ss.map((s) => ribbon.at(s, st.side * (st.backOfCurbU + cutBlend(cuts, s) * (shape.A - st.backOfCurbU))))
    const runId = c.runId
    aprons.push({ ring: [...ss.map((s) => ribbon.at(s, st.side * st.backOfCurbU)), ...[...back].reverse()], runId })
    lines.push({ pts: back, runId })
    // the cut's two ends and the wing ends, across the curb and apron (the score lines a
    // finisher tools at the breaks)
    for (const s of [c.sc - c.half, c.sc + c.half]) if (s > 0 && s < L) lines.push({ pts: [ribbon.at(s, 0), ribbon.at(s, st.side * shape.A)], runId })
    for (const s of [a, b]) if (s > 0 && s < L) lines.push({ pts: [ribbon.at(s, 0), ribbon.at(s, st.side * st.backOfCurbU)], runId })
  }
  return { lines, aprons, walkFrontAt: (s: number) => shape.walkFrontU(cutBlend(cuts, s)) }
}

registerTerrainPadSource((byId) => {
  const out: ReturnType<Parameters<typeof registerTerrainPadSource>[0]> = []
  byId.forEach((el) => {
    if (el.category !== 'paving') return
    const pad = pavingTerrainPad(el, byId)
    if (!pad || pad.ring.length < 3) return
    out.push({
      mod: { id: `paving:${el.id}`, kind: 'pad', refElementId: el.id } as never,
      poly: pad.ring,
      elevFt: Math.min(...pad.ringUnder),
      slopeH: 2,
      line: null,
      pt: null,
      // the content fingerprint (fpModifiers hashes zs): the underside along the ring
      zs: pad.ringUnder,
      isReferencing: true,
      plane: null,
      phaseCreated: (el.phaseCreated as string | undefined) ?? null,
      zFn: pad.under,
      edgeFn: pad.top,
      fromPaving: true,
    })
  })
  return out
})
